import { PRODUCT_STEPS } from '@/lib/products';
import { getCarrierTypesNeedingInput } from '@/types/insurance-builder-legacy';
import { getActionableInsurances } from '@/types/insurance-builder.type';

import { MILESTONES } from '../milestones';
import type { WizardVariantDefinition } from '../wizard';

export const defaultVariant = {
  id: 'default',
  name: 'Standard D2C Intake',
  startPath: '/0',
  milestones: [
    {
      id: MILESTONES.CATEGORY_SELECTED,
      label: 'Choose Your Supplies',
      description: 'Select the types of medical supplies you need',
      startPath: '/0',
      routes: ['/0'],
      isComplete: (state) => state.selectedCategoryIds.length > 0,
    },
    {
      id: MILESTONES.PATIENT_INFO_COLLECTED,
      label: 'Patient Information',
      description: 'Provide basic details about the patient',
      startPath: '/1',
      routes: ['/1'],
      isComplete: (state) =>
        Boolean(
          state.patientRecipient &&
          state.patientFirstName &&
          state.patientLastName &&
          state.patientDateOfBirth &&
          state.patientGender
        ),
    },
    {
      id: MILESTONES.INSURANCE_DETERMINED,
      label: 'Insurance Verification',
      description: 'Confirm your insurance coverage',
      startPath: '/insurance/pre-discovery',
      routes: [
        '/insurance/pre-discovery',
        '/insurance/discovery-pending',
        '/insurance/options',
        '/insurance/input',
        '/insurance/input-confirm',
        '/insurance/verify-pending',
        '/insurance/finalize',
        '/insurance/benefits',
        '/insurance/follow-up',
      ],
      isComplete: (state) => {
        const builder = state.insuranceBuilder;
        const actionable = getActionableInsurances(builder);
        const hasUnprovided = builder.unprovidedInsurances.length > 0;
        const allCarrierTypesAddressed = getCarrierTypesNeedingInput(builder).length === 0;
        return allCarrierTypesAddressed && (actionable.length > 0 || hasUnprovided);
      },
    },
    {
      id: MILESTONES.PROFILE_CREATED,
      label: 'Contact & Shipping',
      description: 'Add your contact info and delivery address',
      startPath: '/insurance/benefits', // Resume at benefits to re-route based on recipient
      routes: [
        '/create-profile/for-me',
        '/create-profile/for-someone-else',
        '/create-profile/for-someone-else/relationship',
        '/create-profile/for-someone-else/shipping',
      ],
      isComplete: (state) =>
        state.insuranceBuilder.unprovidedInsurances.length > 0 ||
        Boolean(
          state.contactEmail &&
          state.contactPhoneNumber &&
          state.contactAddressLine1 &&
          state.contactCity &&
          state.contactState &&
          state.contactZip
        ),
    },
    {
      id: MILESTONES.PRODUCTS_SELECTED,
      label: 'Select Products',
      description: 'Choose your medical supplies',
      startPath: PRODUCT_STEPS[0]?.url ?? '/products/incontinence',
      routes: PRODUCT_STEPS.map((step) => step.url),
      isComplete: (state) => state.productSelectionConfirmed === true,
    },
    {
      id: MILESTONES.APPOINTMENT_RESERVED,
      label: 'Schedule Appointment',
      description: 'Book your telehealth consultation',
      startPath: '/appointment/intro',
      routes: ['/appointment/intro', '/appointment/book-call'],
      isComplete: (state) => !!state.schedulingSelectedSlot,
    },
    {
      id: MILESTONES.PHONE_VERIFIED,
      label: 'Verify Phone Number',
      description: 'Confirm your phone number for the appointment',
      startPath: '/verify-profile/phone-number',
      routes: ['/verify-profile/phone-number', '/verify-profile/phone-number/code'],
      isComplete: (state) => state.contactPhoneNumberVerified === true,
    },
  ],

  exitMilestones: [
    {
      id: MILESTONES.ORDER_COMPLETED,
      label: 'Order Complete',
      description: 'Your order has been submitted',
      route: '/appointment/confirmed',
    },
    {
      id: MILESTONES.WAITLISTED,
      label: 'Waitlist',
      description: 'You have been added to our waitlist',
      route: '/insurance/waitlist/thank-you',
    },
    {
      id: MILESTONES.REACH_OUT_PENDING,
      label: 'We Will Contact You',
      description: 'Our team will reach out to assist you',
      route: '/insurance/reach-out',
    },
  ],
} satisfies WizardVariantDefinition<'default'>;
